// lib
import React, { useCallback, useRef } from 'react'
import { RouteComponentProps } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import GroupMembers from 'src/containers/smart_components/group_member_list/group_member_list'
import PageTitleGroupFeed from 'src/containers/smart_components/page_title/page_title_group_feed'
import LayoutNoMap from 'src/layout/layout_no_map'
import HeaderGroup from '../components/header/subheaders/pages/group/header_group'

// constants & interfaces
import { FEED_DEFAULT_PER_PAGE, GROUP_OVERVIEW, GROUP_PARTICIPATIONS_PAGE_SIZE } from 'src/constants/feed'
import { GROUP_FEED_TITLE } from 'src/constants/meta_tags'

// helpers
import { useGroup } from '@lopo-tech/lopo-api'
import { useHistory } from 'react-router-dom'
import { usePageTracking } from 'src/pages/hooks/hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { FRONTEND_GROUP_PATH } from '../constants/urls'
import { useGetGroupIdFromUrlParams } from '../hooks_shared/use_url_params'

const GroupMembersPage: React.FC<RouteComponentProps> = () => {
  const history = useHistory()
  const user = useSelectUser()
  const slug = useGetGroupIdFromUrlParams()

  const { data, loading, error, fetchMore } = useGroup({
    variables: {
      slug,
      page: 1,
      perPage: FEED_DEFAULT_PER_PAGE,
      groupParticipationsPage: 1,
      groupParticipationsPerPage: GROUP_PARTICIPATIONS_PAGE_SIZE,
      width: 500,
      height: 500
    }
  })

  if (error) {
    console.error('Error fetching group:', error)
  }

  const group = data?.group ?? null

  if (!loading && !group) {
    history.push(FRONTEND_GROUP_PATH)
  }

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (group?.groupParticipations?.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({
        variables: {
          groupParticipationsPage: group?.groupParticipations?.pageInfo.nextPage
        }
      })
    }
  }, [fetchMore, group?.groupParticipations?.pageInfo.nextPage])

  const title = (group && GROUP_FEED_TITLE(group?.name)) ?? undefined

  usePageTracking({ pageTitle: title, user })

  return (
    <>
      <MetaTags
        title={title}
      />
      <LayoutNoMap
        activeNavigationItem={GROUP_OVERVIEW}
        subHeader={<HeaderGroup group={group} showMemberCount />}
        pageTitle={<PageTitleGroupFeed title={title} />}
      >
        <GroupMembers
          currentPage={(group?.groupParticipations?.pageInfo.previousPage ?? 0) + 1}
          loading={loading}
          group={group}
          getNextPage={getNextPage}
        />
      </LayoutNoMap>
    </>
  )
}

export default GroupMembersPage
